import { motion } from "framer-motion";
import { Swords, Coins, Wallet, Trophy } from "lucide-react";
import { useLang, STR } from "@/i18n";

const FEATURE_META = [
  {
    n: "01",
    tk: "f1",
    icon: Swords,
    accent: "text-sky-300",
    chip: "border-sky-400/30 bg-sky-400/10 text-sky-300",
    glow: "group-hover:shadow-[0_20px_80px_-20px_rgba(56,189,248,0.35)] group-hover:border-sky-400/40",
  },
  {
    n: "02",
    tk: "f2",
    icon: Coins,
    accent: "text-amber-300",
    chip: "border-amber-400/30 bg-amber-400/10 text-amber-300",
    glow: "group-hover:shadow-[0_20px_80px_-20px_rgba(251,191,36,0.35)] group-hover:border-amber-400/40",
  },
  {
    n: "03",
    tk: "f3",
    icon: Wallet,
    accent: "text-emerald-300",
    chip: "border-emerald-400/30 bg-emerald-400/10 text-emerald-300",
    glow: "group-hover:shadow-[0_20px_80px_-20px_rgba(52,211,153,0.3)] group-hover:border-emerald-400/40",
  },
  {
    n: "04",
    tk: "f4",
    icon: Trophy,
    accent: "text-orange-300",
    chip: "border-orange-400/30 bg-orange-400/10 text-orange-300",
    glow: "group-hover:shadow-[0_20px_80px_-20px_rgba(251,146,60,0.3)] group-hover:border-orange-400/40",
  },
];

export default function Manifesto() {
  const { lang } = useLang();
  const t = STR[lang];
  return (
    <section id="manifesto" className="relative py-24 sm:py-32 overflow-hidden">
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[40rem] h-[40rem] rounded-full bg-blue-700/10 blur-[160px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="mono text-xs uppercase tracking-[0.3em] text-slate-500 mb-4"
        >
          {t.man_eyebrow}
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight max-w-2xl"
        >
          {t.man_h2a}{" "}
          <span className="bg-gradient-to-r from-sky-300 to-amber-300 bg-clip-text text-transparent">
            {t.man_h2b}
          </span>
        </motion.h2>

        <div className="mt-14 grid sm:grid-cols-2 gap-5 sm:gap-6">
          {FEATURE_META.map((f, i) => (
            <motion.article
              key={f.n}
              data-testid={`feature-card-${f.n}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.65, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
              whileHover={{ y: -8 }}
              className={`group relative rounded-3xl border border-white/10 bg-[#0c1122]/80 backdrop-blur p-7 sm:p-9 transition-all duration-300 ${f.glow}`}
            >
              <div className="flex items-start justify-between mb-8">
                <span className={`mono font-bold text-5xl sm:text-6xl ${f.accent} opacity-90`}>
                  {f.n}
                </span>
                <span className="grid place-items-center w-12 h-12 rounded-2xl border border-white/10 bg-white/[0.04] transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6">
                  <f.icon className={`w-5 h-5 ${f.accent}`} strokeWidth={2.2} />
                </span>
              </div>
              <span className={`mono inline-block text-[10px] uppercase tracking-[0.22em] border rounded-full px-3 py-1.5 mb-4 ${f.chip}`}>
                {t[`${f.tk}_tag`]}
              </span>
              <h3 className="font-display font-bold text-xl sm:text-2xl text-white mb-3">
                {t[`${f.tk}_title`]}
              </h3>
              <p className="text-sm sm:text-base text-slate-400 leading-relaxed">{t[`${f.tk}_desc`]}</p>
            </motion.article>
          ))}

          <motion.article
            data-testid="tournament-card"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.65, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="group relative rounded-3xl border border-white/10 bg-[#0c1122]/80 backdrop-blur p-7 sm:p-9 transition-all duration-300 sm:col-span-2 hover:border-amber-400/40 hover:shadow-[0_20px_80px_-20px_rgba(251,191,36,0.25)]"
          >
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <span className="mono inline-block text-[10px] uppercase tracking-[0.22em] border rounded-full px-3 py-1.5 mb-4 border-amber-400/30 bg-amber-400/10 text-amber-300">
                  {t.tor_tag}
                </span>
                <h3 className="font-display font-bold text-xl sm:text-2xl text-white mb-3">
                  {t.tor_title}
                </h3>
                <p className="text-sm sm:text-base text-slate-400 leading-relaxed">{t.tor_desc}</p>
                <p className="mono mt-5 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] border border-amber-400/30 bg-amber-400/10 text-amber-300 rounded-full px-4 py-2">
                  <Trophy className="w-4 h-4" />
                  {t.tor_extra}: +500 {t.credits_word}
                </p>
              </div>

              <div className="grid grid-cols-[1fr_auto_1fr_auto_1fr] items-center gap-2 sm:gap-3">
                <div className="flex flex-col gap-2">
                  <p className="mono text-[9px] uppercase tracking-[0.2em] text-slate-500 text-center mb-1">{t.tor_qf}</p>
                  {["Rafael × Camila", "Diego × Bruna", "Felipe × Maria", "Ana × Lucas"].map((m) => (
                    <div key={m} className="rounded-xl border border-white/10 bg-white/[0.03] px-2 py-2 text-center mono text-[10px] text-slate-300">
                      {m}
                    </div>
                  ))}
                </div>
                <div className="w-4 h-px bg-white/15" />
                <div className="flex flex-col gap-2 justify-center">
                  <p className="mono text-[9px] uppercase tracking-[0.2em] text-slate-500 text-center mb-1">{t.tor_sf}</p>
                  {["Rafael × Diego", "Maria × Lucas"].map((m) => (
                    <div key={m} className="rounded-xl border border-sky-400/25 bg-sky-400/5 px-2 py-2.5 text-center mono text-[10px] text-sky-200">
                      {m}
                    </div>
                  ))}
                </div>
                <div className="w-4 h-px bg-white/15" />
                <div className="flex flex-col gap-2 justify-center">
                  <p className="mono text-[9px] uppercase tracking-[0.2em] text-amber-300 text-center mb-1">{t.tor_f}</p>
                  <div className="rounded-xl border border-amber-400/40 bg-amber-400/10 px-2 py-3 text-center">
                    <Trophy className="w-5 h-5 text-amber-300 mx-auto mb-1.5" />
                    <p className="mono text-[11px] font-bold text-amber-300">Rafael</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.article>
        </div>
      </div>
    </section>
  );
}
